import invariant from 'invariant';
import { useCallback, useEffect } from 'react';
import { sprintf } from 'sprintf-js';

import type { ApiOrgId } from 'api/types/ApiTypedId';
import { Button } from 'components/Button/Button';
import { Text } from 'components/Text/Text';
import { useTemporaryStorageContext } from 'containers/TemporaryStorageContext';
import { useUser } from 'contexts/UserEnvironmentContext';
import type { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';
import { useMarkAppliedToast } from 'modules/listing/hooks/useMarkAppliedToast';
import {
  useUserDashboardUserHasAppliedToListing,
  userDashboardCreateListingApplication,
  userDashboardDeleteListingApplication,
} from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import type { ListingType } from 'types/ListingType';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  listingId: string;
  listingType: ListingType;
  orgId?: ApiOrgId;
  orgType?: ApiOrgType;
  className?: string;
};

export function MarkAppliedMenuItem({
  listingId,
  listingType,
  orgId,
  orgType,
  className,
}: Props) {
  const { user } = useUser();
  const { showMarkAppliedToast } = useMarkAppliedToast();
  const { userHasAppliedToListing } =
    useUserDashboardUserHasAppliedToListing(listingId);
  const { saveTemporaryStorage, temporaryStorage } =
    useTemporaryStorageContext();

  const initialValues = temporaryStorage?.initialValues;

  useEffect(() => {
    if (
      initialValues &&
      listingId === initialValues.id &&
      listingType === 'JOB'
    ) {
      userDashboardCreateListingApplication({
        id: listingId,
        type: listingType,
        requestedAttachmentsHash: '',
      })
        .then(() => {
          showMarkAppliedToast();
        })
        .catch(() => {
          // noop
        });
    }
  }, [initialValues, listingId, listingType, showMarkAppliedToast]);

  const onClick = useCallback(async () => {
    invariant(listingType === 'JOB', `listingType must be JOB`);
    if (!user && saveTemporaryStorage) {
      trackEvent('Clicked: Marked Applied (Logged Out)', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      const values = {
        id: listingId,
        requestedAttachmentsHash: '',
        type: listingType,
      };
      const tmpId = await saveTemporaryStorage([
        {
          type: 'LISTING_APPLICATION_VALUES',
          data: values,
        },
      ]);

      const redirectTo = `${window.location.pathname}${
        window.location.search
      }${window.location.search ? '&' : '?'}tmpId=${tmpId || ''}`;

      // TODO: Fix this from eslint v9 update
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await showModal('SIGNUP', {
        title: getText('Just One More Step!'),
        description: (
          <Text mb={4}>
            {sprintf(
              getText(
                'Create an account on Idealist in order to mark this %(listingType)s as applied.',
              ),
              {
                listingType: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
              },
            )}
          </Text>
        ),
        redirectTo,
      });
    } else if (userHasAppliedToListing) {
      trackEvent('Clicked: Marked Not Applied', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      await userDashboardDeleteListingApplication(
        listingId,
        userHasAppliedToListing.applicationId,
      );
    } else {
      trackEvent('Clicked: Marked Applied', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      await userDashboardCreateListingApplication({
        id: listingId,
        type: listingType,
        requestedAttachmentsHash: '',
      });

      showMarkAppliedToast();
    }
  }, [
    listingId,
    listingType,
    orgId,
    orgType,
    saveTemporaryStorage,
    showMarkAppliedToast,
    user,
    userHasAppliedToListing,
  ]);

  if (userHasAppliedToListing) {
    return (
      <Button
        type="button"
        data-qa-id="mark-not-applied-button"
        variant="link-grey"
        onClick={onClick}
        className={className}
        size="small"
      >
        {getText('Mark As Not Applied')}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      data-qa-id="mark-applied-button"
      variant="link-grey"
      onClick={onClick}
      className={className}
      size="small"
      icon={{ type: 'normalized', name: 'simple-check' }}
    >
      {getText('Mark As Applied')}
    </Button>
  );
}
